import { useMemo, useRef, useState } from 'react';
import { Check, Copy, FileUp, Info } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Combobox } from '../../components/ui/Combobox';
import { DatePicker } from '../../components/ui/DatePicker';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  defaultExpiryDateIso,
  expiryDateToMillis,
  formatMb,
  validateHtmlUpload,
} from '../../lib/hostedReport';
import { BUNDLE_LIMITS, isZipFilename, validateBundleUploadSize } from '../../lib/hostedBundle';
import { bundleProgressLabel, bundleProgressPercent, type BundlePhaseView } from '../../lib/hostedBundleView';
import {
  HOSTED_REPORT_DEFAULT_EXPIRY_DAYS,
  HOSTED_REPORT_MAX_BYTES,
  type HostedReportSummary,
} from '../../types/hostedReport';
import type { Account } from '../../types/account';
import { formatDateLabel } from '../../components/ui/DatePicker';
import {
  audienceBlocker,
  PortalPublishSection,
  useAudienceReach,
  type PublishArgs,
} from '../portalLibrary';
import {
  buildPortalPublishPayload,
  portalAccessSentence,
  portalFieldsForSection,
  reachSentence,
  type PortalPublishFields,
} from '../../lib/portalLibrary/staffView';
import type { PortalAudienceReach } from '../../types/portalLibrary';

/**
 * What the dialog asks the page to do. ⚠️ A PORTAL publish carries no expiry and
 * no password: its link expiry IS the tile's end date (brief §3c), and a portal
 * item is not password-protected (brief §8/§10).
 */
export type CreateRequest =
  | {
      kind: 'report';
      title: string;
      accountId: string | null;
      /** null = never expires. */
      expiresAtMs: number | null;
      password: string | null;
      filename: string;
      html: string;
    }
  /**
   * A .zip of a page plus its folders. ⚠️ The File itself travels (never read into
   * memory): the browser uploads it straight to storage. Not offered for the
   * portal, whose publish carries one html string.
   */
  | {
      kind: 'bundle';
      title: string;
      accountId: string | null;
      expiresAtMs: number | null;
      password: string | null;
      file: File;
    }
  | ({ kind: 'portal' } & PublishArgs);

// Tools → HTML Hosting — the create flow.
//
// The primary OUTPUT is the shareable URL, so the dialog does not close on
// success: it swaps to a "here's the link" state with a prominent copy button.
// Closing straight back to the list would make the AM hunt for the thing they
// came here to get.

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
}

export function CreateHostedReportDialog({
  accounts,
  busy,
  errorText,
  created,
  publishedReach = null,
  progress = null,
  entryChoice = null,
  onChooseEntry,
  onCreate,
  onClose,
}: {
  accounts: Account[];
  busy: boolean;
  errorText: string | null;
  /** Set once the upload succeeds — flips the dialog to the share-link state. */
  created: HostedReportSummary | null;
  /** Set when the upload was a portal publish — the reach it went out to. */
  publishedReach?: PortalAudienceReach | null;
  /** Upload / unpack progress for a bundle. */
  progress?: BundlePhaseView | null;
  /** Set when the zip has several top-level pages and staff must pick one. */
  entryChoice?: { reportId: string; candidates: string[] } | null;
  onChooseEntry?: (reportId: string, path: string) => void;
  onCreate: (req: CreateRequest) => void;
  onClose: () => void;
}) {
  const [title, setTitle] = useState('');
  const [accountId, setAccountId] = useState('');
  const [password, setPassword] = useState('');
  const [expiryDate, setExpiryDate] = useState(() =>
    defaultExpiryDateIso(Date.now(), HOSTED_REPORT_DEFAULT_EXPIRY_DAYS),
  );
  // ⚠️ Default OFF: a one-off share is time-limited unless someone decides
  // otherwise. Ticking it hides the date rather than clearing it, so unticking
  // brings back what was there.
  const [neverExpires, setNeverExpires] = useState(false);
  const [file, setFile] = useState<{ name: string; size: number; html: string } | null>(
    null,
  );
  const [bundleFile, setBundleFile] = useState<File | null>(null);
  const [pickedEntry, setPickedEntry] = useState('');
  const [fileError, setFileError] = useState<string | null>(null);
  // "Add to Kyck portal". Default OFF: an ordinary upload is a one-off share.
  const [onPortal, setOnPortal] = useState(false);
  const [portal, setPortal] = useState<PortalPublishFields>(() =>
    portalFieldsForSection('college', Date.now()),
  );
  const reach = useAudienceReach(onPortal ? portal.audience : [], portal.section);
  const [copied, setCopied] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const accountOptions = useMemo(
    () => [
      { value: '', label: 'No client account' },
      ...accounts
        .map((a) => ({ value: a.id, label: a.name, description: a.accountType }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    ],
    [accounts],
  );

  async function onPickFile(f: File | undefined): Promise<void> {
    setFileError(null);
    setFile(null);
    setBundleFile(null);
    if (!f) return;
    // A .zip is a BUNDLE (a page plus its folders). It is checked for size only
    // here and never read: the server inspects every entry after the upload.
    if (isZipFilename(f.name)) {
      const size = validateBundleUploadSize(f.size);
      if (!size.ok) {
        setFileError(size.message);
        return;
      }
      setBundleFile(f);
      setOnPortal(false);
      return;
    }
    // Validated through the SAME shared rules the server enforces, so the AM
    // hears about a zip before waiting on a 20 MB upload that will be refused.
    const check = validateHtmlUpload({
      filename: f.name,
      contentType: f.type,
      byteLength: f.size,
    });
    if (!check.ok) {
      setFileError(check.message);
      return;
    }
    const html = await f.text();
    setFile({ name: f.name, size: f.size, html });
  }

  const expiresAtMs = neverExpires ? null : expiryDateToMillis(expiryDate);
  // A never-expiring link needs no date; a dated one needs a valid date.
  const expiryOk = neverExpires || expiresAtMs !== null;
  const portalPayload = buildPortalPublishPayload(portal);
  // ⚠️ The reach must be KNOWN and non-zero before a portal publish can be
  // confirmed — the number is the control (Amendment 2 §D).
  const portalBlocker = !onPortal
    ? null
    : !portalPayload.ok
      ? portalPayload.message
      : audienceBlocker(portal.audience, reach);
  const isBundle = bundleFile !== null;
  const canSubmit =
    !busy &&
    title.trim().length > 0 &&
    (file !== null || bundleFile !== null) &&
    (onPortal && !isBundle ? portalBlocker === null : expiryOk);
  const progressText = bundleProgressLabel(progress);
  const progressPct = bundleProgressPercent(progress);

  // ---------- success state: the link is the deliverable ----------
  if (created) {
    return (
      <Dialog open onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>
              {created.portalLibraryEntryId
                ? 'Published to the Kyck portal'
                : created.kind === 'bundle'
                  ? 'Bundle hosted'
                  : 'Report hosted'}
            </DialogTitle>
            {created.portalLibraryEntryId && publishedReach && (
              <p className="text-sm font-medium text-slate-900">
                {reachSentence(publishedReach).replace('will appear', 'appears')}{' '}
                {portalAccessSentence(publishedReach)}
              </p>
            )}
            <DialogDescription>
              {created.expiresAt === null ? (
                <>Send this link to the client. It never expires</>
              ) : (
                <>
                  Send this link to the client. It expires on{' '}
                  {formatDateLabel(created.expiresAt.slice(0, 10))}
                </>
              )}
              {created.hasPassword ? ' and needs the password you set.' : '.'}
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2">
            <Input readOnly value={created.url} className="font-mono text-xs" />
            <Button
              type="button"
              onClick={() => {
                void navigator.clipboard.writeText(created.url);
                setCopied(true);
                window.setTimeout(() => setCopied(false), 2000);
              }}
            >
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              {copied ? 'Copied' : 'Copy link'}
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={onClose}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // ---------- create form ----------
  return (
    <Dialog open onOpenChange={(open) => !open && !busy && onClose()}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Host an HTML report</DialogTitle>
          <DialogDescription>
            Upload a report (one .html file, or a .zip of a page and its folders) and get a link you can send to a client.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="hr-file">HTML file or zip bundle</Label>
            <input
              ref={inputRef}
              id="hr-file"
              type="file"
              accept=".html,.htm,.zip,text/html,application/zip"
              className="sr-only"
              disabled={busy}
              onChange={(e) => void onPickFile(e.target.files?.[0])}
            />
            <Button
              type="button"
              variant="outline"
              className="w-full justify-start"
              onClick={() => inputRef.current?.click()}
            >
              <FileUp className="h-4 w-4" />
              {file ? file.name : bundleFile ? bundleFile.name : 'Choose a file…'}
            </Button>
            {file && (
              <p className="text-xs text-slate-500">{formatMb(file.size)}</p>
            )}
            {bundleFile && (
              <p className="text-xs text-slate-500">
                Bundle · {formatMb(bundleFile.size)} zip. Uploaded straight to storage, then unpacked.
              </p>
            )}
            {fileError && <p className="text-xs text-destructive">{fileError}</p>}
            {/*
              The single-file limitation is STATED rather than discovered: a zip
              or a multi-file export is exactly what someone will reach for, and
              finding out after choosing a file is a worse experience than being
              told up front.
            */}
            <p className="flex items-start gap-1.5 text-xs text-slate-500">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
              <span>
                One self-contained .html file (up to {formatMb(HOSTED_REPORT_MAX_BYTES)}), or a
                .zip of a page with its images, video and other files (up to{' '}
                {formatMb(BUNDLE_LIMITS.maxUploadBytes)}). On Windows: right-click the folder,
                Send to, Compressed folder. A zip needs an index.html (or one .html page)
                at its top level.
              </span>
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="hr-title">Title</Label>
            <Input
              id="hr-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Q3 performance review"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="hr-account">Client account (optional)</Label>
            <Combobox
              id="hr-account"
              value={accountId}
              onValueChange={setAccountId}
              options={accountOptions}
              placeholder="No client account"
              searchPlaceholder="Search accounts…"
              aria-label="Client account"
            />
          </div>

          <label className="flex items-center gap-2 text-sm font-medium text-slate-900">
            <Checkbox
              checked={onPortal && !isBundle}
              disabled={isBundle}
              onCheckedChange={(v) => setOnPortal(v === true)}
              aria-label="Add to Kyck portal"
            />
            Add to Kyck portal
          </label>
          {isBundle && (
            <p className="text-xs text-slate-500">
              A zip bundle can be added to the portal after it is hosted (use Add to portal on its row).
            </p>
          )}

          {onPortal && !isBundle && (
            <PortalPublishSection value={portal} onChange={setPortal} accounts={accounts} />
          )}

          {/*
            ⚠️⚠️ REMOVED, not disabled, for a portal item (brief §3c): the link's
            expiry IS the tile's end date. A disabled copy of the same date is a
            second control, and a second control is how the drift comes back.
            The password goes with it — a portal item is not password-protected.
          */}
          {(!onPortal || isBundle) && (
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="hr-expiry">Expires</Label>
              {!neverExpires && (
                <DatePicker
                  id="hr-expiry"
                  value={expiryDate}
                  onChange={setExpiryDate}
                  minIso={todayIso()}
                  ariaLabel="Expiry date"
                />
              )}
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <Checkbox
                  checked={neverExpires}
                  onCheckedChange={(v) => setNeverExpires(v === true)}
                  aria-label="Never expire"
                />
                Never expire
              </label>
              <p className="text-xs text-slate-500">
                {neverExpires
                  ? 'The link keeps working until you revoke it.'
                  : expiresAtMs
                    ? `The link stops working after ${formatDateLabel(expiryDate)}.`
                    : 'Pick a valid date.'}
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="hr-password">Password (optional)</Label>
              <Input
                id="hr-password"
                type="text"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Leave blank for no password"
              />
              <p className="text-xs text-slate-500">
                Anyone with the link can open the report unless you set one.
              </p>
            </div>
          </div>
          )}

          {onPortal && !isBundle && portalBlocker && (
            <p className="text-xs text-slate-500">{portalBlocker}</p>
          )}

          {progressText && (
            <div className="space-y-1.5" role="status" aria-live="polite">
              <p className="text-sm text-slate-700">{progressText}</p>
              {progressPct !== null && (
                <div className="h-2 w-full overflow-hidden rounded bg-slate-100">
                  <div className="h-full bg-kyck-600 transition-all" style={{ width: `${progressPct}%` }} />
                </div>
              )}
            </div>
          )}

          {entryChoice && (
            <fieldset className="space-y-1.5 rounded border border-slate-200 p-3">
              <legend className="px-1 text-sm font-medium text-slate-900">Which page should open first?</legend>
              {entryChoice.candidates.map((c) => (
                <label key={c} className="flex items-center gap-2 text-sm text-slate-700">
                  <input type="radio" name="hr-entry" value={c} checked={pickedEntry === c} onChange={() => setPickedEntry(c)} />
                  {c}
                </label>
              ))}
              <Button
                type="button"
                size="sm"
                disabled={!pickedEntry || busy}
                onClick={() => onChooseEntry?.(entryChoice.reportId, pickedEntry)}
              >
                Use this page
              </Button>
            </fieldset>
          )}

          {errorText && <p className="whitespace-pre-line text-sm text-destructive">{errorText}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            disabled={!canSubmit || entryChoice !== null}
            onClick={() => {
              if (bundleFile) {
                onCreate({
                  kind: 'bundle',
                  title: title.trim(),
                  accountId: accountId || null,
                  expiresAtMs,
                  password: password.trim() ? password : null,
                  file: bundleFile,
                });
                return;
              }
              if (onPortal) {
                if (!portalPayload.ok) return;
                onCreate({
                  kind: 'portal',
                  ...portalPayload.payload,
                  title: title.trim(),
                  accountId: accountId || null,
                  filename: file!.name,
                  html: file!.html,
                });
                return;
              }
              onCreate({
                kind: 'report',
                title: title.trim(),
                accountId: accountId || null,
                expiresAtMs,
                password: password.trim() ? password : null,
                filename: file!.name,
                html: file!.html,
              });
            }}
          >
            {busy ? (isBundle ? 'Working…' : 'Uploading…') : onPortal && !isBundle ? 'Publish to portal' : isBundle ? 'Host bundle' : 'Host report'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
